import { matchPath } from 'react-router';
import i18n, { DEFAULT_LANGUAGE, SUPPORTED_LANGUAGES } from './i18n';
import { appRoutes, dynamicRoutesKeys } from './app_routes';

const HOME_PATHS = ['/', ''];

const migratedRouteKeys = [
  appRoutes.home,
  appRoutes.psychics,
  appRoutes.advisor
];

const stripLocalePrefix = (pathname) => {
  const match = matchPath('/:lang/*', pathname);
  const lang = match?.params?.lang;

  if (!lang || lang === DEFAULT_LANGUAGE || !SUPPORTED_LANGUAGES.includes(lang)) return pathname;

  return `/${ match.params['*'] || '' }`;
};

const normalizePath = (pathname) => {
  const [withoutQuery] = (pathname || '').split(/[?#]/);
  const withoutTrailingSlash = withoutQuery.length > 1 ? withoutQuery.replace(/\/+$/, '') : withoutQuery;

  return stripLocalePrefix(withoutTrailingSlash);
};

const getRoutePatterns = (routeKey) => SUPPORTED_LANGUAGES
  .map(lng => i18n.t(routeKey, { lng }))
  .filter(pattern => pattern && pattern !== routeKey);

const getCategoryPatterns = () => dynamicRoutesKeys
  .flatMap(routeKey => getRoutePatterns(routeKey));

/**
 * Category pages are served by the Next.js `[category]` catch-all, but the SPA only knows the
 * category slugs exposed at runtime through `window.config.uiSettings.routes`. A slug the SPA
 * does not know must still hard-navigate, otherwise the catch-all route in `localized_switch`
 * bounces the user back to the home page.
 */
export const isMigratedCategoryPath = (pathname) => {
  const path = normalizePath(pathname);

  if (HOME_PATHS.includes(path)) return false;
  if (path.split('/').filter(Boolean).length !== 1) return false;

  return getCategoryPatterns().some(pattern => matchPath(pattern, path));
};

/**
 * True when `pathname` points at a page that has moved to the Next.js app, so navigating to it
 * needs a document load instead of a client-side transition.
 */
export const isMigratedPath = (pathname) => {
  const path = normalizePath(pathname);

  if (HOME_PATHS.includes(path)) return true;

  const matchesKnownRoute = migratedRouteKeys
    .flatMap(routeKey => getRoutePatterns(routeKey))
    .some(pattern => matchPath(pattern, path));

  if (matchesKnownRoute) return true;

  return isMigratedCategoryPath(path);
};
